<template>
  <div class="order-main-info">
    <div class="info-columns">
      <div class="info-column">
        <!-- Основная информация -->
        <OrderInfoCard title="Основная информация">
          <template #icon>
            <AppIcon name="info" :size="18" />
          </template>

            <!-- Номер заказа -->
            <OrderInfoField label="Номер заказа:" :value="order.number" inline-on-wide />

            <OrderInfoField
              label="Дата заказа:"
              :value="getDateText(order.order_date)"
              inline-on-wide
            />

            <!-- Статус заказа -->
            <OrderInfoField label="Статус заказа:" inline-on-wide>
              <BaseStatusSelect
                :value="order.status"
                :label="orderStore.formatOrderStatus(order.status)"
                :color="orderStore.orderStatusColor(order.status)"
                :options="orderStore.orderStatusSelectOptions"
                @change="$emit('status-changed', $event)"
              />
            </OrderInfoField>

            <!-- Тип заказа -->
            <OrderInfoField
              v-if="order.order_type || orderTypeOptions.length"
              label="Тип заказа:"
              inline-on-wide
            >
              <BaseStatusSelect
                :value="
                  order.order_type
                    ? String(order.order_type.id)
                    : null
                "
                :label="
                  order.order_type ? order.order_type.name : '—'
                "
                :color="
                  order.order_type
                    ? order.order_type.color
                    : '#9ca3af'
                "
                :options="orderStore.orderTypeSelectOptions"
                @change="$emit('order-type-changed', $event)"
              />
            </OrderInfoField>

            <OrderInfoField label="Путевой лист:" inline-on-wide>
              <span
                v-if="order.waybill"
                class="waybill-link"
                @click="goToWaybill(order.waybill.id)"
              >
                {{ order.waybill.number || `#${order.waybill.id}` }}
              </span>
              <span v-else>—</span>
            </OrderInfoField>

            <!-- Ответственный -->
            <OrderInfoField label="Ответственный:" inline-on-wide>
              <UserIdentityCard
                :user="order.responsible_user"
                :name="order.responsible_user?.initials"
                :user-id="
                  order.responsible_user?.id ||
                  order.responsible_user_id
                "
                class="responsible-user-card"
              />
            </OrderInfoField>
        </OrderInfoCard>

        <!-- Данные о грузе -->
        <OrderInfoCard title="Данные о грузе">
          <template #icon>
            <AppIcon name="cargo" :size="18" />
          </template>

          <OrderInfoField
            label="Название груза:"
            :value="order.cargo_name"
            info-key="order.cargo"
            :filled="Boolean(order.cargo_name)"
            inline-on-wide
          />

          <OrderInfoField
            label="Вес груза (кг):"
            :value="order.cargo_weight"
            info-key="order.cargo"
            :filled="Boolean(order.cargo_weight)"
            inline-on-wide
          />

          <OrderInfoField
            label="Объем груза (м³):"
            :value="order.cargo_volume"
            info-key="order.cargo"
            :filled="Boolean(order.cargo_volume)"
            inline-on-wide
          />
        </OrderInfoCard>

        <!-- Исполнение -->
        <OrderInfoCard
          title="Исполнение"
          :count="orderTrips.length > 0 ? orderTrips.length : null"
        >
          <template #icon>
            <AppIcon name="truck" :size="18" />
          </template>

          <div
            v-if="orderTrips.length > 0"
            class="entities-list"
          >
              <OrderExecutionCard
                v-for="trip in orderTrips"
                :key="trip.id"
                :trip="trip"
                :company-id="companyId"
                @open="$emit('go-to-trip-details', trip.id)"
              />
          </div>
          <div v-else class="no-counterparties">
            <AppIcon name="truck" :size="24" />
            <span>Рейс ещё не создан</span>
          </div>
        </OrderInfoCard>

        <!-- Контактные данные -->
        <OrderInfoCard title="Контактные данные">
          <template #icon>
            <AppIcon name="contact" :size="18" />
          </template>

          <OrderInfoField label="Отправитель:" :value="order.sender" inline-on-wide />

          <OrderInfoField label="Получатель:" :value="order.receiver" inline-on-wide />
        </OrderInfoCard>

        <!-- Комментарии -->
        <OrderInfoCard title="Комментарии">
          <template #icon>
            <AppIcon name="comment" :size="18" />
          </template>

          <OrderInfoField
            label="Комментарий:"
            :value="order.comment"
            info-key="order.comment"
            :filled="Boolean(order.comment)"
            inline-on-wide
          />
        </OrderInfoCard>

      </div>

      <div class="info-column">
        <!-- Цены -->
        <OrderInfoCard title="Цены">
          <template #icon>
            <AppIcon name="payment" :size="18" />
          </template>

          <OrderInfoField
            label="Цена (руб.):"
            :value="formatBigNumber(order.price, true)"
            info-key="order.payment"
            :filled="Boolean(order.price)"
            inline-on-wide
          />

          <OrderInfoField
            label="Форма оплаты:"
            :value="orderStore.formatPaymentForm(order.payment_form)"
            info-key="order.payment"
            :filled="Boolean(order.payment_form)"
            inline-on-wide
          />

          <OrderInfoField label="Статус оплаты:" inline-on-wide>
              <BaseStatusSelect
                :value="order.payment_status"
                :label="
                  orderStore.formatPaymentStatus(order.payment_status)
                "
                :color="
                  orderStore.paymentStatusColor(order.payment_status)
                "
                :options="orderStore.paymentStatusSelectOptions"
                @change="$emit('payment-status-changed', $event)"
              />
          </OrderInfoField>
        </OrderInfoCard>

        <!-- Контрагенты -->
        <OrderInfoCard
          title="Контрагенты"
          :count="orderCounterparties.length > 0 ? orderCounterparties.length : null"
        >
          <template #icon>
            <AppIcon name="participants" :size="18" />
          </template>

          <div
            v-if="orderCounterparties.length > 0"
            class="entities-list"
          >
              <OrderCounterpartyCard
                v-for="counterparty in orderCounterparties"
                :key="counterparty.id"
                :role-label="getRoleLabel(counterparty.role)"
                :role-class="getRoleClass(counterparty.role)"
                :name="getCounterpartyName(counterparty.counterparty_id)"
                @open="
                  goToCounterpartyDetails(
                    counterparty.counterparty_id
                  )
                "
              />
          </div>
          <div v-else class="no-counterparties">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 12H16M3 12H5M19 12H21M12 3V5M12 19V21M18.364 5.63604L16.9497 7.05025M7.05025 16.9497L5.63604 18.364M18.364 18.364L16.9497 16.9497M7.05025 7.05025L5.63604 5.63604"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
            <span>Контрагенты не&nbsp;назначены</span>
          </div>
        </OrderInfoCard>

        <!-- Время -->
        <OrderInfoCard title="Время">
          <template #icon>
            <AppIcon name="time" :size="18" />
          </template>

          <OrderInfoField
            label="Планируемое время погрузки:"
            :value="getDateTimeText(order.planned_pickup_at)"
            inline-on-wide
          />

          <OrderInfoField
            label="Планируемое время доставки:"
            :value="getDateTimeText(order.planned_delivery_at)"
            inline-on-wide
          />

          <OrderInfoField
            v-if="['completed', 'canceled'].includes(order.status)"
            label="Фактическое время погрузки:"
            :value="getDateTimeText(order.actual_pickup_at)"
            inline-on-wide
          />

          <OrderInfoField
            v-if="['completed', 'canceled'].includes(order.status)"
            label="Фактическое время разгрузки:"
            :value="getDateTimeText(order.actual_delivery_at)"
            inline-on-wide
          />
        </OrderInfoCard>

        <!-- Температурные требования -->
        <OrderInfoCard title="Температурные требования">
          <template #icon>
            <AppIcon name="temperature" :size="18" />
          </template>

          <OrderInfoField
            label="Мин. температура (°C):"
            :value="order.min_temperature"
            inline-on-wide
          />

          <OrderInfoField
            label="Макс. температура (°C):"
            :value="order.max_temperature"
            inline-on-wide
          />
        </OrderInfoCard>

      </div>
    </div>

    <div
      class="meta-section"
      v-if="
        order.created_at ||
        order.updated_at ||
        order.created_by?.initials
      "
    >
      <MetaTime
        :createdAt="order.created_at"
        :updatedAt="order.updated_at"
      />
      <span class="meta-created-by" v-if="order.created_by?.initials">
        Создал: {{ order.created_by.initials }}
      </span>
    </div>
  </div>
</template>

<script>
import { useOrderStore } from "@/stores/orderStore";
import { useCounterpartyStore } from "@/stores/counterpartyStore";

import { mapState } from "pinia";

import { formatDateAndTime, formatDate } from "@/utils/dateUtils";
import { formatBigNumber } from "@/utils/numberUtils";

import MetaTime from "@/components/Meta/MetaTime.vue";
import UserIdentityCard from "@/components/User/UserIdentityCard.vue";
import AppIcon from "@/components/Icon/AppIcon.vue";
import OrderInfoCard from "@/components/Order/OrderInfoCard.vue";
import OrderInfoField from "@/components/Order/OrderInfoField.vue";
import OrderCounterpartyCard from "@/components/Order/OrderCounterpartyCard.vue";
import OrderExecutionCard from "@/components/Order/OrderExecutionCard.vue";

export default {
  name: "OrderMainInfo",
  components: {
    MetaTime,
    UserIdentityCard,
    AppIcon,
    OrderInfoCard,
    OrderInfoField,
    OrderCounterpartyCard,
    OrderExecutionCard,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
    editOrderForm: {
      type: Object,
      required: true,
    },
    orderTrips: {
      type: Array,
      default: () => [],
    },
    companyId: {
      type: [Number, String],
      required: true,
    },
  },
  emits: [
    "create-trip",
    "go-to-trip-details",
    "status-changed",
    "payment-status-changed",
    "order-type-changed",
  ],
  computed: {
    ...mapState(useCounterpartyStore, ["counterparties"]),

    orderStore() {
      return useOrderStore();
    },

    orderTypeOptions() {
      return this.orderStore.orderTypeSelectOptions || [];
    },

    orderCounterparties() {
      return this.order.order_counterparties || [];
    },
  },
  methods: {
    goToWaybill(waybillId) {
      this.$router.push({
        name: "WaybillDetails",
        params: { id: waybillId },
      });
    },

    getDateTimeText(date) {
      return date ? formatDateAndTime(date) : "—";
    },

    getDateText(date) {
      return date ? formatDate(date) : "—";
    },

    getCounterpartyName(counterpartyId) {
      if (!counterpartyId) return "—";

      const counterparty = this.counterparties.find(
        (c) => c.id === counterpartyId
      );

      if (counterparty) {
        const name = counterparty.name || "";
        const inn = counterparty.inn ? ` - ${counterparty.inn}` : "";
        return `${name}${inn}`;
      }

      return `Контрагент #${counterpartyId}`;
    },

    getRoleLabel(role) {
      const roleLabels = {
        customer: "Заказчик",
        shipper: "Грузоотправитель",
        consignee: "Грузополучатель",
        carrier: "Перевозчик",
        forwarder: "Экспедитор",
      };
      return roleLabels[role] || role;
    },

    getRoleClass(role) {
      const roleClasses = {
        customer: "role-customer",
        shipper: "role-shipper",
        consignee: "role-consignee",
        carrier: "role-carrier",
        forwarder: "role-forwarder",
      };
      return roleClasses[role] || "";
    },

    goToCounterpartyDetails(counterpartyId) {
      this.$router.push({
        name: "CounterpartyDetails",
        params: { id: counterpartyId },
      });
    },

    formatBigNumber,
  },
};
</script>

<style scoped>
.order-main-info {
  width: 100%;
}

.info-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--base-in-indent);
  align-items: flex-start;
}

.info-column {
  display: flex;
  flex-direction: column;
  gap: var(--base-in-indent);
}

.responsible-user-card {
  justify-content: flex-end;
  max-width: 260px;
}

.waybill-link {
  color: #4f46e5;
  cursor: pointer;
  text-decoration: underline;
}

.entities-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.no-counterparties {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 30px 20px;
  color: #999;
  text-align: center;
}

.no-counterparties svg {
  color: #ccc;
}

.meta-section {
  margin-top: var(--base-in-indent);
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}

.meta-created-by {
  color: #666;
  font-style: italic;
  font-size: 14px;
  text-align: right;
}

@media (max-width: 1200px) {
  .responsible-user-card {
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .meta-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .meta-created-by {
    text-align: left;
  }

  .info-columns {
    grid-template-columns: 1fr;
    gap: var(--base-in-indent);
  }

  .entities-list {
    gap: 10px;
  }
}
</style>
